import { observer } from 'mobx-react-lite';
import { CalendarSurfaceItemContentProps } from './CalendarSurfaceItem.types';
import {
    renderCharmWrapper,
    renderIconWrapper,
    renderJoinButton,
} from '../CalendarSurfaceItemComponentRenderer';
import * as React from 'react';
import {
    getCalendarEventItemSubjectText,
    getCalendarEventItemLocationText,
} from 'owa-calendar-event-attributes';
import { isEventJoinableNow } from 'owa-online-meeting/lib/bootIndex';
import { useCustomAnimationFrame } from 'owa-react-hooks/lib/useCustomAnimationFrame';
import getInnerWidth from '../../utils/getInnerWidth';
import createSubjectGridStyle from '../../utils/createSubjectGridStyle';

const classNames = require('classnames');
const styles = require('../CalendarSurfaceItem.scss');

/**
 * State for the CalendarSurfaceItemUnder60
 */
export interface CalendarSurfaceItem30OrUnderState {
    itemWidth: number;
    isJoinableNow: boolean;
    joinButtonWidth: number;
}

/**
 * * The Component for a Calendar Surface item under 60 minutes long
 */
export default observer(function CalendarSurfaceItemUnder60(
    props: CalendarSurfaceItemContentProps
) {
    const wrapperRef = React.useRef<HTMLDivElement>();
    const joinWrapperRef = React.useRef<HTMLDivElement>();

    const [itemWidth, setItemWidth] = React.useState<number>(0);
    const [isJoinableNow, setIsJoinableNow] = React.useState<boolean>(false);
    const [joinButtonWidth, setJoinButtonWidth] = React.useState<number>(0);

    const currIsJoinableNow = props.joinButtonVisible && isEventJoinableNow(props.item);
    let currJoinButtonWidth = joinWrapperRef.current ? getInnerWidth(joinWrapperRef.current) : 0;
    let currItemWidth = wrapperRef.current ? getInnerWidth(wrapperRef.current) : 0;

    React.useEffect(() => {
        if (isJoinableNow != currIsJoinableNow) {
            setIsJoinableNow(currIsJoinableNow);
        }
    });

    const resetFieldSizing = () => {
        currJoinButtonWidth = joinWrapperRef.current ? getInnerWidth(joinWrapperRef.current) : 0;
        currItemWidth = wrapperRef.current ? getInnerWidth(wrapperRef.current) : 0;

        if (itemWidth != currItemWidth) {
            setItemWidth(currItemWidth);
        }
        if (joinButtonWidth != currJoinButtonWidth) {
            setJoinButtonWidth(currJoinButtonWidth);
        }
    };

    const [requestFieldSizingFrame] = useCustomAnimationFrame();
    React.useLayoutEffect(() => {
        requestFieldSizingFrame(resetFieldSizing);
    }, []);

    const { item, colorScheme, charmId, allowEmptySubject } = props;
    const subject = getCalendarEventItemSubjectText(item, allowEmptySubject);
    const location = getCalendarEventItemLocationText(item);
    const organizer = item.Organizer && item.Organizer.Mailbox.Name;
    const gridGapStyle = currIsJoinableNow ? styles.headerContainerGridGap : null;

    return (
        <>
            <div
                className={classNames(
                    styles.itemContent,
                    styles.calendarItemClippingLine,
                    styles.singleLineItem
                )}
                ref={wrapperRef}>
                <div
                    className={classNames(styles.headerContainer, gridGapStyle)}
                    style={createSubjectGridStyle(
                        currItemWidth,
                        currJoinButtonWidth,
                        currIsJoinableNow
                    )}>
                    <h2 className={classNames(styles.itemSubject, styles.truncatedSubject)}>
                        {renderCharmWrapper(item, colorScheme, charmId)}
                        <span>{subject}</span>
                    </h2>
                    {currIsJoinableNow ? (
                        <div ref={joinWrapperRef} className={styles.joinButtonWrapper}>
                            {renderJoinButton(
                                item,
                                colorScheme,
                                currItemWidth,
                                currJoinButtonWidth,
                                styles.joinButton
                            )}
                        </div>
                    ) : null}
                </div>
                {location && (
                    <div
                        className={classNames(
                            styles.locOrgMarginUnder60,
                            styles.itemElementContent
                        )}>
                        {location}
                    </div>
                )}
                {item.IsMeeting && organizer && (
                    <span
                        className={
                            location
                                ? styles.itemElementContent
                                : classNames(styles.itemElementContent, styles.locOrgMarginUnder60)
                        }>
                        {organizer}
                    </span>
                )}
            </div>
            {!currIsJoinableNow && renderIconWrapper(item, true)}
        </>
    );
});
